import React from 'react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { createUser, listUsers, updateUser, User, CreateUserRequest, UpdateUserRequest } from './api';
import UserModal from './UserModal';
import { useBreadcrumbs } from 'contexts/BreadcrumbsContext';
import dayjs from "dayjs"
import localizedFormat from "dayjs/plugin/localizedFormat"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "components/ui/command";
import { Check, ChevronsUpDown } from 'lucide-react';
import { Division, fetchDivisions } from '../ara/api';
import { cn } from 'lib/utils';
import { listRoles } from '../roles/api';

dayjs.extend(localizedFormat)

type Role = {
  role_id: number;
  role_name: string;
};

const UsersPage: React.FC = () => {
  const { setBreadcrumbs } = useBreadcrumbs();
  const [query, setQuery] = React.useState<string>('');
  const [limit, setLimit] = React.useState<number>(20);
  const [offset, setOffset] = React.useState<number>(0);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>('');
  const [users, setUsers] = React.useState<User[]>([]);
  const [total, setTotal] = React.useState<number>(0);

  const [divisions, setDivisions] = React.useState<Division[]>([]);
  const [selectedDivision, setSelectedDivision] = React.useState<string>('');
  const [divisionOpen, setDivisionOpen] = React.useState(false);

  const [roles, setRoles] = React.useState<Role[]>([]);
  const [selectedRole, setSelectedRole] = React.useState<number | null>(null);
  const [roleOpen, setRoleOpen] = React.useState(false);

  const [modalOpen, setModalOpen] = React.useState<boolean>(false);
  const [editingUser, setEditingUser] = React.useState<User | null>(null);

  const selectedDivisionObj = divisions.find(d => d.ou_code === selectedDivision);
  const selectedRoleObj = roles.find(r => r.role_id === selectedRole);

  const fetchData = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await listUsers({
        q: query.trim() || undefined,
        limit,
        offset,
        division: selectedDivision ?? undefined,
        role_id: selectedRole ?? undefined,
      });
      setUsers(data.items);
      setTotal(data.total);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [query, limit, offset, selectedDivision, selectedRole]);

  React.useEffect(() => {
    setBreadcrumbs([{ label: 'Settings', href: '#' }, { label: 'User Management' }]);
    fetchData();
  }, [fetchData, setBreadcrumbs]);

  React.useEffect(() => {
    const loadDivisions = async () => {
      try {
        const resp = await fetchDivisions();
        setDivisions(resp.items);
        if (resp.items.length === 1) {
          setSelectedDivision(resp.items[0].ou_code);
        }
      } catch {
        setDivisions([]);
      }
    };
    loadDivisions();
  }, []);

  React.useEffect(() => {
    const loadRoles = async () => {
      try {
        const resp = await listRoles();
        setRoles(resp.items);
      } catch {
        setRoles([]);
      }
    };

    loadRoles();
  }, []);


  const handleDivisionChange = (divisionOuCode: string) => {
    setSelectedDivision(divisionOuCode);
  };

  const handleRoleChange = (roleId: number | null) => {
    setSelectedRole(roleId);
    setOffset(0);
  };

  const handleSearch = () => {
    setOffset(0);
    fetchData();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const openCreateModal = () => {
    setEditingUser(null);
    setModalOpen(true);
  };

  const openEditModal = (user: User) => {
    setEditingUser(user);
    setModalOpen(true);
  };

  const handleModalSubmit = async (formData: CreateUserRequest | UpdateUserRequest, isEdit: boolean) => {
    if (isEdit) {
      const updateData = formData as UpdateUserRequest;
      await updateUser(editingUser!.ecode, updateData);
    } else {
      const createData = formData as CreateUserRequest;
      await createUser(createData);
    }
    fetchData();
  };

  const page = Math.floor(offset / limit) + 1;
  const pageCount = Math.max(1, Math.ceil(total / limit));

  const handlePrevPage = () => {
    setOffset(Math.max(0, offset - limit));
  };

  const handleNextPage = () => {
    setOffset(offset + limit);
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setOffset(0);
  };

  return (
    <div className="space-y-6 mx-auto w-full max-w-screen-2xl 2xl:max-w-[2000px] px-6 sm:px-8">
      {/* Search Section */}
      <div className="flex items-end gap-4 w-full">
        <Input
          placeholder="Search users..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          className="max-w-sm"
        />

        <div className="min-w-[200px]">
          <label className="block text-sm font-medium mb-1 text-gray-700">
            Division
          </label>
          <Popover open={divisionOpen} onOpenChange={setDivisionOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                disabled={divisions.length === 1}
                className="w-full justify-between h-10 px-2.5 text-sm"
              >
                {selectedDivisionObj ? selectedDivisionObj.ou_name : "-- select --"}
                <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
              </Button>
            </PopoverTrigger>

            <PopoverContent className="w-[250px] p-0">
              <Command>
                <CommandInput placeholder="Search division..." />
                <CommandEmpty>No division found.</CommandEmpty>
                <CommandGroup>
                  {divisions.map((div) => (
                    <CommandItem
                      key={div.ou_code}
                      value={div.ou_name}
                      onSelect={() => {
                        const newValue = selectedDivision === div.ou_code ? '' : div.ou_code;
                        handleDivisionChange(newValue);
                        setDivisionOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          selectedDivision === div.ou_code ? "opacity-100" : "opacity-0"
                        )}
                      />
                      {div.ou_name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        <div className="min-w-[200px]">
          <label className="block text-sm font-medium mb-1 text-gray-700">
            Role
          </label>
          <Popover open={roleOpen} onOpenChange={setRoleOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                className="w-full justify-between h-10 px-2.5 text-sm"
              >
                {selectedRoleObj ? selectedRoleObj.role_name : "-- select --"}
                <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
              </Button>
            </PopoverTrigger>

            <PopoverContent className="w-[250px] p-0">
              <Command>
                <CommandInput placeholder="Search role..." />
                <CommandEmpty>No role found.</CommandEmpty>
                <CommandGroup>
                  {roles.map((role) => (
                    <CommandItem
                      key={role.role_id}
                      value={role.role_name}
                      onSelect={() => {
                        const newValue =
                          selectedRole === role.role_id ? null : role.role_id;
                        handleRoleChange(newValue);
                        setRoleOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          selectedRole === role.role_id ? "opacity-100" : "opacity-0"
                        )}
                      />
                      {role.role_name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        <Button onClick={openCreateModal} size="sm" className="ml-auto h-10">
          Create User
        </Button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      {/* Table Section */}
      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[120px]">Ecode</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Role</TableHead>
              <TableHead className="min-w-[150px]">Division</TableHead>
              <TableHead>Reporting Manager</TableHead>
              <TableHead>Access</TableHead>
              <TableHead className="min-w-[180px]">Last Login</TableHead>
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} className="h-24 text-center">
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mr-2"></div>
                    Loading users...
                  </div>
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="h-24 text-center">
                  <div className="flex flex-col items-center justify-center text-muted-foreground">
                    <p className="text-sm">No users found</p>
                    {query && <p className="text-xs mt-1">Try adjusting your search terms</p>}
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user.ecode}>
                  <TableCell className="font-medium">{user.ecode}</TableCell>
                  <TableCell>{user.full_name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.mobile_no}</TableCell>
                  <TableCell>
                    <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">
                      {user.role_name}
                    </span>
                  </TableCell>
                  <TableCell>
                    {user.division_name ? (
                      <span className="inline-flex items-center rounded-full bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700 ring-1 ring-inset ring-purple-700/10">
                        {user.division_name}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">None</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {user.reporting_manager_name ? (
                      <span className="text-sm">{user.reporting_manager_name}</span>
                    ) : (
                      <span className="text-xs text-muted-foreground">None</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="max-w-xs">
                      {user.access_ous.length === 0 ? (
                        <span className="text-xs text-muted-foreground">All access</span>
                      ) : (
                        <span className="text-xs text-foreground truncate" title={user.access_ous.map(ou => ou.ou_name).join(', ')}>
                          {user.access_ous.length === 1
                            ? user.access_ous[0].ou_name
                            : user.access_ous.length <= 2
                              ? user.access_ous.map(ou => ou.ou_name).join(', ')
                              : `${user.access_ous[0].ou_name} +${user.access_ous.length - 1} more`
                          }
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {user.last_login_at ? dayjs(user.last_login_at).format("DD MMM YYYY h:mm A") : "Never"}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditModal(user)}
                    >
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination Section */}
      <div className="flex items-center justify-between px-2">
        <div className="flex items-center gap-6 text-sm text-muted-foreground">
          <div>
            Showing {users.length === 0 ? 0 : offset + 1} to {Math.min(offset + limit, total)} of {total} users
          </div>
          <div className="flex items-center gap-2">
            <span>Rows per page:</span>
            <select
              className="h-8 w-16 rounded border border-input bg-background px-2 text-sm"
              value={limit}
              onChange={(e) => handleLimitChange(Number(e.target.value))}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrevPage}
            disabled={offset === 0}
          >
            Previous
          </Button>
          <div className="flex items-center gap-1 text-sm">
            <span>Page</span>
            <span className="font-medium">{page}</span>
            <span>of</span>
            <span className="font-medium">{pageCount}</span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleNextPage}
            disabled={offset + limit >= total}
          >
            Next
          </Button>
        </div>
      </div>

      {/* Modal */}
      <UserModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        user={editingUser}
        onSubmit={handleModalSubmit}
      />
    </div>
  );
};

export default UsersPage;